/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Ionicons } from '@expo/vector-icons';
import { Text, TouchableOpacity, View } from 'react-native';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { ModalShell } from '../../../shared/modals/ModalShell'
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { styles as posUi } from '../posShared.styles';

import type { PosStockNoticeVariant } from '../../domain/posTypes';

export type { PosStockNoticeVariant };

type PosStockNoticeModalProps = {
  visible: boolean;
  variant: PosStockNoticeVariant;
  title: string;
  body: string;
  canAdd: boolean;
  onClose: () => void;
  onCorrectStock: () => void;
  onAddAnyway: () => void;
};

const VARIANT_META: Record<
  PosStockNoticeVariant,
  { kicker: string; icon: keyof typeof Ionicons.glyphMap; iconColor: string }
> = {
  block: { kicker: 'Sin stock', icon: 'ban-outline', iconColor: ticketColors.bad },
  warn: { kicker: 'Revisá el stock', icon: 'warning-outline', iconColor: ticketColors.gold },
  allow: { kicker: 'Sin stock', icon: 'cube-outline', iconColor: ticketColors.gold },
};

export const PosStockNoticeModal = ({
  visible,
  variant,
  title,
  body,
  canAdd,
  onClose,
  onCorrectStock,
  onAddAnyway,
}: PosStockNoticeModalProps) => {
  const meta = VARIANT_META[variant];
  const panelStyle = variant === 'block' ? modalExperience.stockNoticePanelBlocked : modalExperience.stockNoticePanel;
  const iconWrapStyle =
    variant === 'block'
      ? [modalExperience.stockNoticeIconWrap, modalExperience.stockNoticeIconWrapBlocked]
      : modalExperience.stockNoticeIconWrap;

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      keyboardAvoiding={false}
      widthVariant="standard"
      footer={
        <View style={modalExperience.stockNoticeActions}>
          {canAdd ? (
            <TouchableOpacity
              style={posUi.posPrimaryCta}
              onPress={onAddAnyway}
              accessibilityRole="button"
              accessibilityLabel="Agregar producto sin stock al carrito"
            >
              <Text style={posUi.posPrimaryCtaText}>Agregar igual</Text>
            </TouchableOpacity>
          ) : null}

          <View style={modalExperience.stockNoticeActions}>
            <TouchableOpacity
              style={posUi.secondaryButton}
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Cerrar aviso de stock"
            >
              <Text style={posUi.secondaryButtonText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                posUi.secondaryButton,
                !canAdd && { backgroundColor: ticketColors.gold, borderColor: ticketColors.gold },
              ]}
              onPress={onCorrectStock}
              accessibilityRole="button"
              accessibilityLabel="Ir a Stock para reponer este producto"
            >
              <Text
                style={
                  canAdd ? posUi.secondaryButtonText : [posUi.secondaryButtonText, { color: ticketColors.goldInk }]
                }
              >
                Ir a Stock
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      }
    >
      <View style={modalExperience.stockNoticeHero}>
        <View style={iconWrapStyle}>
          <Ionicons name={meta.icon} size={22} color={meta.iconColor} />
        </View>
        <View style={modalExperience.stockNoticeHeroText}>
          <Text style={modalExperience.stockNoticeKicker}>{meta.kicker}</Text>
          <Text style={modalExperience.stockNoticeTitle}>{title}</Text>
        </View>
      </View>

      <View style={panelStyle}>
        <Text style={modalExperience.stockNoticeBody}>{body}</Text>
      </View>
    </ModalShell>
  );
};
